import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import HeaderOne from "../components/HeaderOne";
import FooterAreaOne from "../components/FooterAreaOne";
import Breadcrumb from "../components/Breadcrumb";
import SubscribeOne from "../components/SubscribeOne";
import ServiceDetails from "../components/ServiceDetails";
import RentalCarDetails from "../components/RentalCarDetails";
import Preloader from "../helper/Preloader";
import useInitialLoad from "../helper/useInitialLoad";

// Import data arrays for non-database services
import { CHAPA_SERVICES_DATA } from "../data/chapaData";
import { MECANICA_SERVICES_DATA } from "../data/mecanicaData";
import { MANTENIMIENTO_SERVICES_DATA } from "../data/mantenimientoData";
import NotFound from "../components/NotFound";

// Category config for breadcrumbs
const CATEGORY_CONFIG = {
  "chapa-y-pintura": { label: "Chapa y Pintura", path: "/chapa-y-pintura" },
  "mecanica": { label: "Mecánica", path: "/mecanica" },
  "mantenimiento": { label: "Mantenimiento", path: "/mantenimiento" },
  "alquiler": { label: "Alquiler", path: "/alquiler" }
};

const ServiceDetailsPage = () => {
  // For preloader - only shows on initial app load
  const showPreloader = useInitialLoad(1000);

  // Grab both category and slug from the URL
  const { category, slug } = useParams();

  // State for rental car data (fetched from API)
  const [rentalCar, setRentalCar] = useState(null);
  const [rentalLoading, setRentalLoading] = useState(category === "alquiler");
  const [rentalNotFound, setRentalNotFound] = useState(false);

  // Fetch rental car data from API when category is alquiler
  useEffect(() => {
    if (category === "alquiler" && slug) {
      setRentalLoading(true);
      setRentalNotFound(false);
      fetch(`/api/rental-cars/${slug}`)
        .then(res => res.json())
        .then(data => {
          // Expect a consistent API shape: { car: { ... } }
          const carData = data && data.car ? data.car : null;
          if (carData && carData.slug && carData.title) {
            setRentalCar(carData);
          } else {
            setRentalNotFound(true);
          }
        })
        .catch(() => {
          setRentalNotFound(true);
        })
        .finally(() => {
          setRentalLoading(false);
        });
    }
  }, [category, slug]);

  // Get category info for breadcrumbs
  const categoryInfo = CATEGORY_CONFIG[category];
  const isRentalCar = category === "alquiler";

  // For rental cars, use the fetched data
  if (isRentalCar) {
    // Build breadcrumb parents
    const breadcrumbParents = [
      { label: "Servicios", path: "/#servicios" },
      ...(categoryInfo ? [categoryInfo] : [])
    ];

    if (rentalLoading) {
      return (
        <>
          {showPreloader && <Preloader />}
          <HeaderOne />
          <Breadcrumb title="Cargando..." parents={breadcrumbParents} />
          <div style={{ padding: "4rem", textAlign: "center" }}>
            <div className="rental-loading">
              <div className="rental-loading__spinner"></div>
              <p>Cargando vehículo...</p>
            </div>
          </div>
          <SubscribeOne />
          <FooterAreaOne />
        </>
      );
    }

    if (rentalNotFound || !rentalCar) {
      return (
        <>
          {showPreloader && <Preloader />}
          <HeaderOne />
          <Breadcrumb
            title="Vehículo no encontrado"
            parents={breadcrumbParents}
          />
          <div style={{ padding: "2rem" }}>
            <NotFound />
          </div>
          <SubscribeOne />
          <FooterAreaOne />
        </>
      );
    }

    return (
      <>
        {showPreloader && <Preloader />}
        <HeaderOne />
        <Breadcrumb title={rentalCar.title} parents={breadcrumbParents} />
        <RentalCarDetails car={rentalCar} />
        <SubscribeOne />
        <FooterAreaOne />
      </>
    );
  }

  // For non-rental services, use static data
  let dataList = [];
  if (category === "chapa-y-pintura") {
    dataList = CHAPA_SERVICES_DATA;
  } else if (category === "mecanica") {
    dataList = MECANICA_SERVICES_DATA;
  } else if (category === "mantenimiento") {
    dataList = MANTENIMIENTO_SERVICES_DATA;
  }

  // Find the service by slug
  const service = dataList.find((item) => item.slug === slug);

  // Build breadcrumb parents
  const breadcrumbParents = [
    { label: "Servicios", path: "/#servicios" },
    ...(categoryInfo ? [categoryInfo] : [])
  ];

  // If not found, show not found page
  if (!service) {
    return (
      <>
        {showPreloader && <Preloader />}
        <HeaderOne />
        <Breadcrumb
          title="Servicio no encontrado"
          parents={[{ label: "Servicios", path: "/#servicios" }]}
        />
        <div style={{ padding: "2rem" }}>
          <NotFound/>
        </div>
        <SubscribeOne />
        <FooterAreaOne />
      </>
    );
  }

  return (
    <>
      {showPreloader && <Preloader />}
      <HeaderOne />
      <Breadcrumb title={service.title} parents={breadcrumbParents} />
      <ServiceDetails service={service} />
      <SubscribeOne />
      <FooterAreaOne />
    </>
  );
};

export default ServiceDetailsPage;
